import { useMemo } from 'react';
import {
  useBreakpoint,
  useCompareMode,
  useComparingConversationsParams,
  useCurrentConversation,
  useLeftPanelState,
  useRightSidePanelState,
  useConversationParams,
} from 'src/hooks';
import { MainNavToggleButton } from 'src/pages/ManageTasksChatPage/components/ConversationsHeader/components/MainNavToggleButton';
import styles from './ConversationsHeader.module.scss';
import { ChatThreadModelSelector } from 'src/v2/components/ChatThreadModelSelector';
import cn from 'classnames';
import { convertTaskHashToTitle } from 'src/utils';
import { ConversationMenu } from 'src/v2/components/ConversationsList/components/ConversationMenu';
import { Button } from 'src/v2/commonComponents/Button';
import { ArrowLeft, CaretLeft } from '@phosphor-icons/react';
import { SUPER_PROJECT_NAME, SVG_SIZE_M } from 'src/constants';
import { AddConversationButton } from '../../../../v2/components/AddConversationButton';
import { CompareModeButton } from 'src/v2/components/CompareModeView/components/CompareModeButton';
import AuthContext from 'src/contexts/AuthContext';
import { useContext } from 'react';
import { useChatHistory } from 'src/hooks';
import { AppRoutes } from 'src/types';
import { env } from 'src/env';
import { useLocation } from 'react-router-dom';

export const ConversationsHeader = () => {
  const { isNinjaRetired } = useChatHistory();
  const { isLandingPage } = useConversationParams();
  const { isMobileOrTablet, isDesktopAndUp } = useBreakpoint();
  const { isLeftPanelExpanded } = useLeftPanelState();
  const { isRightPanelExpanded } = useRightSidePanelState();
  const { firstComparedConvId, isCompareMode } =
    useComparingConversationsParams();
  const { pathname } = useLocation();
  const { currentConversation } = useCurrentConversation(firstComparedConvId);
  const { resetCompareMode } = useCompareMode();
  const { isGuestAccess } = useContext(AuthContext);
  const headerTitle = useMemo(() => {
    return isCompareMode && !firstComparedConvId
      ? ''
      : currentConversation?.conversation_hash?.replace('#', '') || '';
  }, [isCompareMode, firstComparedConvId, currentConversation]);
  const isChatHistory = pathname === AppRoutes.CHAT_HISTORY;

  const onBackToSNClick = () => {
    window.location.href = env.REACT_APP_SUPER_NINJA_URL;
  };

  return (
    <header className={styles.root}>
      {isMobileOrTablet && <MainNavToggleButton />}

      <div className={styles.leftSideButtons}>
        {!isNinjaRetired && !isCompareMode && <ChatThreadModelSelector />}

        {!isNinjaRetired && isCompareMode && (
          <>
            <Button color="transparent" onClick={resetCompareMode}>
              <ArrowLeft size={SVG_SIZE_M} />
              <span className={styles.compareBackText}>Compare mode view</span>
            </Button>
          </>
        )}

        {isNinjaRetired &&
          !isCompareMode &&
          (isChatHistory || !isLandingPage) && (
            <Button color="transparent" onClick={onBackToSNClick}>
              <CaretLeft size={SVG_SIZE_M} />
              <span className={styles.backButtonText}>
                Back to {SUPER_PROJECT_NAME}
              </span>
            </Button>
          )}
      </div>

      {headerTitle &&
        isDesktopAndUp &&
        !(isLeftPanelExpanded && isRightPanelExpanded) && (
          <h4 className={styles.desktopTitle}>
            {convertTaskHashToTitle(headerTitle)}
          </h4>
        )}
      {isDesktopAndUp && isLandingPage && isGuestAccess && (
        <div className={styles.links}>
          <Button
            as="button"
            onClick={() => {
              window.open(
                'https://www.ninjatech.ai/product/super-ninja',
                '_blank',
                'noopener,noreferrer',
              );
            }}
            color="link"
          >
            Intro
          </Button>
          <Button
            as="button"
            onClick={() => {
              window.open(
                'https://www.ninjatech.ai/product/benchmarks',
                '_blank',
                'noopener,noreferrer',
              );
            }}
            color="link"
          >
            Benchmark
          </Button>
          <Button
            as="button"
            onClick={() => {
              window.open(
                'https://www.ninjatech.ai/pricing',
                '_blank',
                'noopener,noreferrer',
              );
            }}
            color="link"
          >
            Pricing
          </Button>
        </div>
      )}
      <div
        className={cn(styles.actionWrapper, styles.rightSideButtons, {
          [styles.actionWrapperDesktopWidth]:
            !isMobileOrTablet && !isRightPanelExpanded, // todo it is temporary till we implement active button here
        })}
      >
        <CompareModeButton />

        {!!currentConversation && !isCompareMode && (
          <ConversationMenu conversation={currentConversation} align="end" />
        )}

        {!isNinjaRetired && !isCompareMode && isMobileOrTablet && (
          <AddConversationButton appearance="gradient-plus-button" />
        )}
      </div>
    </header>
  );
};
